import React, { useEffect, useRef, useState } from 'react'
import { Table, Button, Tag, Space, message, Tooltip, Modal, Input, Dropdown, MenuProps } from 'antd'
import {
  EyeOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  ReloadOutlined,
  WarningOutlined,
  PrinterOutlined,
  MoreOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { Order } from '../../Types/order.interface'
import OrderDetailsModal from './OrderDetailsModal'
import './OrderTable.scss'
import { OrderStatus } from '../../Types/api-structures'

interface OrderTableProps {
  orders: Order[]
  showActions?: boolean
  actionConfig?: {
    buttonText: string
    nextStatus: OrderStatus
    onAction: (
      orderId: string,
      nextStatus: OrderStatus,
      otp?: string,
      tableNumber?: string
    ) => Promise<void>
  }
  onRefreshOrder?: (orderId: string) => Promise<void>
  onPrintKOT?: (orderId: string, tableNumber?: string, empNo?: string) => Promise<void>
  onPrintBill?: (orderId: string, tableNumber?: string, empNo?: string) => Promise<void>
}

interface OrderWithCountdown extends Order {
  countdown: string
  sortTime: number
}

const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  showActions = false,
  actionConfig,
  onRefreshOrder,
  onPrintKOT,
  onPrintBill
}) => {
  const [ordersWithCountdown, setOrdersWithCountdown] = useState<
    OrderWithCountdown[]
  >([])
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null)
  const [refreshingOrderId, setRefreshingOrderId] = useState<string | null>(
    null
  )
  const [printingOrderId, setPrintingOrderId] = useState<string | null>(null)
  const [printingBillOrderId, setPrintingBillOrderId] = useState<string | null>(null)

  // Print modal state
  const [printModalOrderId, setPrintModalOrderId] = useState<string | null>(null)
  const [printModalType, setPrintModalType] = useState<'KOT' | 'BILL' | null>(null)
  const [printTableNumber, setPrintTableNumber] = useState('')
  const [printEmpNo, setPrintEmpNo] = useState('')

  // OTP modal state (for READY → SERVING)
  const [otpModalOrderId, setOtpModalOrderId] = useState<string | null>(null)
  const [otpValue, setOtpValue] = useState('')
  const [tableNumber, setTableNumber] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const otpInputRef = useRef<any>(null)

  // Scroll indicator state
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true) // assume true initially until measured
  const tableRef = useRef<HTMLDivElement>(null)

  const checkScroll = () => {
    if (tableRef.current) {
      const scrollContainer = (
        tableRef.current.querySelector('.ant-table-body') ||
        tableRef.current.querySelector('.ant-table-content') ||
        tableRef.current.querySelector('.ant-table-wrapper')
      ) as HTMLElement
      if (scrollContainer) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer
        setCanScrollLeft(scrollLeft > 0)
        setCanScrollRight(Math.ceil(scrollLeft) + clientWidth < scrollWidth - 1)
      }
    }
  }

  useEffect(() => {
    const timers = [
      setTimeout(checkScroll, 100),
      setTimeout(checkScroll, 500),
      setTimeout(checkScroll, 1000),
    ]
    window.addEventListener('resize', checkScroll)
    
    const scrollContainer = (
      tableRef.current?.querySelector('.ant-table-body') ||
      tableRef.current?.querySelector('.ant-table-content')
    )
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScroll)
    }

    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener('resize', checkScroll)
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScroll)
      }
    }
  }, [ordersWithCountdown])

  // Robustly parse reach time and calculate countdown and sort time
  const calculateCountdown = (
    orderReachTime: number | string
  ): { countdown: string; sortTime: number } => {
    const now = Date.now()

    // Parse orderReachTime supporting ISO, ms epoch, and s epoch
    let reachMs: number
    const numeric = Number(orderReachTime)
    if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
      // Treat values < 1e12 as seconds, otherwise milliseconds
      reachMs = numeric < 1e12 ? numeric * 1000 : numeric
    } else {
      const parsed = Date.parse(orderReachTime.toString())
      reachMs = Number.isNaN(parsed) ? now : parsed
    }

    const difference = reachMs - now

    if (difference <= 0) {
      return { countdown: 'Order time passed', sortTime: 0 }
    }

    const hours = Math.floor(difference / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    const countdown = `${hours > 0 ? `${hours}h ` : ''}${minutes}m ${seconds}s`
    return { countdown, sortTime: difference }
  }

  // Update countdown every second
  useEffect(() => {
    const updateCountdowns = () => {
      const updatedOrders = orders.map((order) => {
        const { countdown, sortTime } = calculateCountdown(order.orderReachTime)
        return {
          ...order,
          countdown,
          sortTime
        }
      })

      // Sort by sortTime (ascending - nearest first)
      updatedOrders.sort((a, b) => a.sortTime - b.sortTime)
      setOrdersWithCountdown(updatedOrders)
    }

    // Initial update
    updateCountdowns()

    // Update every second
    const interval = setInterval(updateCountdowns, 1000)

    return () => clearInterval(interval)
  }, [orders])

  // Derive the currently selected order from the live orders prop
  const selectedOrder = React.useMemo(() => {
    if (!selectedOrderId) return null
    return orders.find((o) => o.orderId === selectedOrderId) || null
  }, [selectedOrderId, orders])

  const handleViewDetails = (order: Order) => {
    setSelectedOrderId(order.orderId)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedOrderId(null)
  }

  const handleStatusChange = async (orderId: string) => {
    if (!actionConfig) return

    // For READY → SERVING, show OTP modal instead
    if (actionConfig.nextStatus === 'SERVING') {
      setOtpValue('')
      setTableNumber('')
      setOtpModalOrderId(orderId)
      setTimeout(() => otpInputRef.current?.focus(), 100)
      return
    }

    setLoadingOrderId(orderId)
    try {
      await actionConfig.onAction(orderId, actionConfig.nextStatus)
      message.success(`Order moved to ${actionConfig.nextStatus}`)
    } catch (error) {
      message.error('Failed to update order status')
      console.error('Error updating order status:', error)
    } finally {
      setLoadingOrderId(null)
    }
  }

  const handleOtpSubmit = async () => {
    if (!actionConfig || !otpModalOrderId) return
    if (!otpValue.trim()) {
      message.warning('Please enter the OTP')
      return
    }
    setOtpLoading(true)
    try {
      await actionConfig.onAction(
        otpModalOrderId,
        OrderStatus.SERVING,
        otpValue.trim(),
        tableNumber.trim()
      )
      message.success('Order moved to SERVING')
      setOtpModalOrderId(null)
      setOtpValue('')
      setTableNumber('')
    } catch (error) {
      message.error('Failed to update order status')
      console.error('Error updating order status:', error)
    } finally {
      setOtpLoading(false)
    }
  }

  const handleMoveToPreparation = async (orderId: string) => {
    if (!actionConfig) return

    setLoadingOrderId(orderId)
    try {
      await actionConfig.onAction(orderId, OrderStatus.PREPARING)
      message.success('Order moved to PREPARING')
    } catch (error) {
      message.error('Failed to move order to PREPARING')
      console.error('Error updating order status:', error)
    } finally {
      setLoadingOrderId(null)
    }
  }

  const handleRefreshOrder = async (orderId: string) => {
    if (!onRefreshOrder) return

    setRefreshingOrderId(orderId)
    try {
      await onRefreshOrder(orderId)
      message.success('Order details refreshed')
    } catch (error) {
      message.error('Failed to refresh order details')
      console.error('Error refreshing order:', error)
    } finally {
      setRefreshingOrderId(null)
    }
  }

  const handlePrintKOTClick = (orderId: string) => {
    if (!onPrintKOT) return
    setPrintModalOrderId(orderId)
    setPrintModalType('KOT')
    setPrintTableNumber('')
    setPrintEmpNo('')
  }

  const handlePrintBillClick = (orderId: string) => {
    if (!onPrintBill) return
    setPrintModalOrderId(orderId)
    setPrintModalType('BILL')
    setPrintTableNumber('')
    setPrintEmpNo('')
  }

  const handleConfirmPrint = async () => {
    if (!printModalOrderId || !printModalType) return

    const tNo = printTableNumber.trim() || undefined
    const eNo = printEmpNo.trim() || undefined

    if (printModalType === 'KOT' && onPrintKOT) {
      setPrintingOrderId(printModalOrderId)
      setPrintModalOrderId(null)
      try {
        await onPrintKOT(printModalOrderId, tNo, eNo)
        message.success('KOT sent to kitchen printer')
      } catch (error) {
        message.error('Failed to print KOT')
        console.error('Error printing KOT:', error)
      } finally {
        setPrintingOrderId(null)
      }
    } else if (printModalType === 'BILL' && onPrintBill) {
      setPrintingBillOrderId(printModalOrderId)
      setPrintModalOrderId(null)
      try {
        await onPrintBill(printModalOrderId, tNo, eNo)
        message.success('Bill sent to invoice printer')
      } catch (error) {
        message.error('Failed to print Bill')
        console.error('Error printing Bill:', error)
      } finally {
        setPrintingBillOrderId(null)
      }
    }
  }

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      PAYMENTCOMPLETED: 'blue',
      ONLAZYTRACK: 'cyan',
      PREPARING: 'orange',
      READY: 'green',
      SERVING: 'purple',
      COMPLETED: 'success'
    }
    return statusColors[status.toUpperCase()] || 'default'
  }

  const columns: ColumnsType<OrderWithCountdown> = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 110,
      fixed: 'left',
      render: (text: string, record: OrderWithCountdown) => {
        const shortText = text.length > 8 ? `${text.substring(0, 8)}...` : text;
        return (
          <Space>
            <Tooltip title={text}>
              <span style={{ fontWeight: 600, color: '#1890ff', cursor: 'pointer' }}>{shortText}</span>
            </Tooltip>
            {record.hasPartialFood && (
              <Tooltip title='Partial food availability - Click refresh to update'>
                <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 150,
      render: (status: string, record: OrderWithCountdown) => (
        <Space direction='vertical' size={2}>
          <Tag
            color={getStatusColor(status)}
            style={{ fontSize: '13px', padding: '4px 12px' }}
          >
            {status?.toUpperCase() === 'ONLAZYTRACK' ? 'On the way' : status}
          </Tag>
          {record.highlightLevel && (
            <div style={{ textAlign: 'center' }}>
              <Tag
                color={record.highlightLevel === 'reached' ? 'green' : 'blue'}
                style={{
                  fontSize: '10px',
                  borderRadius: '10px',
                  margin: 0,
                  textTransform: 'uppercase',
                  fontWeight: 'bold'
                }}
              >
                {record.highlightLevel}
              </Tag>
            </div>
          )}
        </Space>
      )
    },
    {
      title: 'Time to Reach',
      dataIndex: 'countdown',
      key: 'countdown',
      width: 150,
      render: (countdown: string, record: OrderWithCountdown) => {
        if (
          record.orderStatus?.toUpperCase() === 'COMPLETED' ||
          record.orderStatus?.toUpperCase() === 'SERVING'
        ) {
          return <span style={{ color: '#8c8c8c' }}>-</span>
        }
        return (
          <span
            style={{
              fontWeight: 600,
              color:
                record.sortTime < 300000
                  ? '#ff4d4f'
                  : record.sortTime < 600000
                    ? '#faad14'
                    : '#52c41a'
            }}
          >
            {countdown}
          </span>
        )
      }
    },
    {
      title: 'Service Type',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 140,
      render: (type: string) => <Tag>{type}</Tag>
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: string) => (
        <span style={{ fontWeight: 600, fontSize: '14px' }}>
          ₹{parseFloat(amount).toFixed(2)}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: showActions && actionConfig ? 160 : 100,
      fixed: 'right',
      render: (_, record: OrderWithCountdown) => {
        const menuItems: MenuProps['items'] = []

        if (record.hasPartialFood && onRefreshOrder) {
          menuItems.push({
            key: 'refresh',
            label: 'Refresh',
            icon: <ReloadOutlined />,
            onClick: () => handleRefreshOrder(record.orderId),
            danger: true
          })
        }

        if (
          onPrintKOT &&
          ['PREPARING', 'READY', 'SERVING'].includes(
            (record.orderStatus || '').toUpperCase()
          )
        ) {
          menuItems.push({
            key: 'printKOT',
            label: 'Print KOT',
            icon: <PrinterOutlined />,
            onClick: () => handlePrintKOTClick(record.orderId)
          })
        }

        if (
          onPrintBill &&
          ['PREPARING', 'READY', 'SERVING', 'COMPLETED'].includes(
            (record.orderStatus || '').toUpperCase()
          )
        ) {
          menuItems.push({
            key: 'printBill',
            label: 'Print Bill',
            icon: <PrinterOutlined />,
            onClick: () => handlePrintBillClick(record.orderId)
          })
        }

        if (
          actionConfig &&
          record.orderStatus?.toUpperCase() === 'ONLAZYTRACK' &&
          (record.sortTime <= 0 || record.countdown === 'Order time passed')
        ) {
          menuItems.push({
            key: 'prep',
            label: 'Prep Order',
            icon: <ArrowRightOutlined />,
            onClick: () => handleMoveToPreparation(record.orderId)
          })
        }

        return (
          <Space size='small' direction='horizontal' style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Tooltip title="View Details">
              <Button 
                icon={<EyeOutlined />} 
                onClick={() => handleViewDetails(record)}
                style={{ borderRadius: '6px' }}
              />
            </Tooltip>

            {showActions && actionConfig && (
              <Tooltip title={actionConfig.buttonText}>
                <Button
                  type='primary'
                  className='responsive-action-btn'
                  onClick={() => handleStatusChange(record.orderId)}
                  loading={loadingOrderId === record.orderId}
                  style={{
                    borderRadius: '6px',
                    background:
                      actionConfig.nextStatus === 'COMPLETED'
                        ? '#52c41a'
                        : '#1890ff'
                  }}
                >
                  <span className="action-text">
                    {actionConfig.buttonText.replace(/Move to /i, '')}
                  </span>
                  <span className="action-icon">
                    {actionConfig.nextStatus === 'COMPLETED' ? <CheckOutlined /> : <ArrowRightOutlined />}
                  </span>
                </Button>
              </Tooltip>
            )}

            {menuItems.length > 0 && (
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <Button icon={<MoreOutlined />} style={{ borderRadius: '6px' }} />
              </Dropdown>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <>
      <div className="desktop-table-container">
        <div
          className={`table-scroll-wrapper${canScrollLeft ? ' can-scroll-left' : ''}${canScrollRight ? ' can-scroll-right' : ''}`}
          ref={tableRef}
        >
          <Table
            columns={columns}
            dataSource={ordersWithCountdown}
            rowKey='orderId'
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} orders`,
              pageSizeOptions: ['5', '10', '20', '50']
            }}
            scroll={{ x: 1000 }}
            className='order-table'
            rowClassName={(record) => {
              if (record.highlightLevel === 'reached') return 'reached-row'
              if (record.highlightLevel === 'nearby') return 'nearby-row'
              if (record.sortTime < 300000) return 'urgent-row'
              if (record.sortTime < 600000) return 'warning-row'
              return ''
            }}
          />
        </div>
      </div>

      <div className="mobile-card-container">
        {ordersWithCountdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>No data</div>
        ) : (
          ordersWithCountdown.map((record) => {
            const rowClass = (() => {
              if (record.highlightLevel === 'reached') return 'reached-row'
              if (record.highlightLevel === 'nearby') return 'nearby-row'
              if (record.sortTime < 300000) return 'urgent-row'
              if (record.sortTime < 600000) return 'warning-row'
              return ''
            })();

            return (
              <div key={record.orderId} className={`mobile-order-card ${rowClass}`}>
                <div className="card-header">
                  <span className="order-id">#{record.orderId}</span>
                  <span className="order-status">{record.orderStatus}</span>
                  {record.hasPartialFood && <WarningOutlined style={{ color: '#faad14' }} />}
                </div>
                <div className="card-body">
                  <div>{record.serviceType}, {record.countdown}</div>
                </div>
                <div className="card-footer">
                  <span className="total-amount">Total {record.totalAmount}</span>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {showActions && actionConfig && (
                      <Button
                        type='primary'
                        size="small"
                        onClick={() => handleStatusChange(record.orderId)}
                        loading={loadingOrderId === record.orderId}
                        style={{
                          borderRadius: '4px',
                          background: actionConfig.nextStatus === 'COMPLETED' ? '#52c41a' : '#1890ff'
                        }}
                      >
                        {actionConfig.buttonText}
                      </Button>
                    )}
                    <span className="view-btn" onClick={() => handleViewDetails(record)}>View</span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {selectedOrder && (
        <OrderDetailsModal
          visible={isModalVisible}
          order={selectedOrder}
          onClose={handleCloseModal}
        />
      )}

      {/* OTP Modal for READY → SERVING */}
      <Modal
        title='Enter Order OTP'
        open={otpModalOrderId !== null}
        onOk={handleOtpSubmit}
        onCancel={() => {
          setOtpModalOrderId(null)
          setOtpValue('')
          setTableNumber('')
        }}
        okText='Confirm'
        cancelText='Cancel'
        confirmLoading={otpLoading}
        centered
      >
        <p style={{ marginBottom: 12 }}>
          Please enter the OTP provided by the customer to start serving order.
        </p>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input
            ref={otpInputRef}
            placeholder='Enter OTP'
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            maxLength={10}
            size='large'
            style={{ letterSpacing: '0.2em', fontWeight: 600 }}
          />
          <Input
            placeholder='Enter Table Number (Optional)'
            value={tableNumber}
            onChange={(e) => setTableNumber(e.target.value)}
            onPressEnter={handleOtpSubmit}
            size='large'
          />
        </Space>
      </Modal>

      {/* Print Options Modal */}
      <Modal
        title={`Print Options - ${printModalType}`}
        open={printModalOrderId !== null}
        onOk={handleConfirmPrint}
        onCancel={() => {
          setPrintModalOrderId(null)
          setPrintModalType(null)
          setPrintTableNumber('')
          setPrintEmpNo('')
        }}
        okText='Print'
        cancelText='Cancel'
        centered
      >
        <p style={{ marginBottom: 12 }}>
          You can optionally provide a Table Number and/or Employee Number to be printed on the receipt.
        </p>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input
            placeholder='Table Number (Optional)'
            value={printTableNumber}
            onChange={(e) => setPrintTableNumber(e.target.value)}
            size='large'
          />
          <Input
            placeholder='Employee Number (Optional)'
            value={printEmpNo}
            onChange={(e) => setPrintEmpNo(e.target.value)}
            onPressEnter={handleConfirmPrint}
            size='large'
          />
        </Space>
      </Modal>
    </>
  )
}

export default OrderTable
